import fetch from '@adc/fetch'

/**
 * Get bidding status through bid-mapper service. Req can be passed in as null, just needs listingId. Should return
 * response in same format as v1 bid-status endpoint.
 * @param {null | Request} req - is optional and is null for most calls
 * @param {Record<string, string>} opts - required for endpoint to work, should be something like {listingId:'12345'}
 * @returns {Promise}
 */
export const getBiddingStatus = (req, opts = { listingId: null }) =>
  fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .get(
      `/resi/online-auction/v1/legacy-bidding/listings/${opts.listingId}/bid-status`,
      { req }
    )
    .then(({ body }) => body)

/**
 * Fetches the bidding history for a certain listingId. req is optional and can be passed in as null and size is defaulted to 3.
 * @param {null | Request} req is optional and is null for most calls
 * @param {Record<string, string>} opts - required for endpoint to work, should be something like {listingId:'12345'} or {listingId:'12345',size:'5'} for custom size
 * @returns {Promise}
 */
export const getBiddingHistory = (req, opts = {}) => {
  // this is done to keep the default size of "3" in the object even if it's not specified in the opts input param
  const mergedOpts = { ...{ listingId: null, size: '3' }, ...opts }
  return fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .get(
      `/resi/online-auction/v1/legacy-bidding/listings/${mergedOpts.listingId}/bids`,
      { req, queryParams: { size: mergedOpts.size } }
    )
    .then(({ body }) => body)
}

/**
 * Use the api mapper service to get the bidding pre-auction dates which currently happens as part of the initial bidding-status call.
 * req can be passed as null, all that's needed for this is a valid listingId param
 * @param {null | Request} req is optional and is null for most calls
 * @param {Record<string, string>} opts - required for endpoint to work, should be something like {listingId:'12345'}
 * @returns {Promise}
 */
export const getBiddingPreAuctionDates = (req, opts = { listingId: null }) =>
  fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .get(
      `/resi/online-auction/v1/legacy-bidding/listings/${opts.listingId}/pre-auction-dates`,
      { req }
    )
    .then(({ body }) => body)

/**
 * Submit a bid through the api mapper bidding service which expects
 * a listing id and a bid object, example bid object is { bid_amount: 50000 }.
 * @param {null | Request} req is optional and is null for most calls
 * @param {Record<string, any>} opts - required for endpoint to work, should be something like {listingId:'12345',bid:{bid_amount:'20000'}}
 * @returns {Promise}
 */
export const performBid = (req, opts = {}) => {
  const mergedOpts = {
    ...{ listingId: null, bid: { bid_amount: null } },
    ...opts,
  }
  return fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .post(
      `/resi/online-auction/v1/legacy-bidding/listings/${mergedOpts.listingId}/bids`,
      {
        body: mergedOpts.bid,
      }
    )
    .then(({ body }) => body)
}

/**
 * Submit a proxy bid through the api mapper bidding service which expects
 * a listing id and a bid object, example bid object is { bid_amount: 50000 }.
 * @param {null | Request} req is optional and is null for most calls
 * @param {Record<string, any>} opts - required for endpoint to work, should be something like {listingId:'12345',bid:{bid_amount:'20000'}}
 * @returns {Promise}
 */
export const performProxyBid = (req, opts = {}) => {
  const mergedOpts = {
    ...{ listingId: null, bid: { bid_amount: null } },
    ...opts,
  }
  return fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .post(
      `/resi/online-auction/v1/legacy-bidding/listings/${mergedOpts.listingId}/max-bids`,
      {
        body: mergedOpts.bid,
      }
    )
    .then(({ body }) => body)
}

/**
 * Get bidding status through bid-mapper service for a collection of listings at the same time.
 *
 * @param {null | Request} req
 * @param {Record<string, string>} opts
 * @returns {Promise}
 */
export const getBiddingStatusCollection = (req, opts = { listingIds: [] }) =>
  fetch
    .fromService('RESI_MS_ONLINE_AUCTION')
    .post('/resi/online-auction/v1/legacy-bidding/listings/list/bid-status', {
      body: { listing_ids: opts.listingIds },
      req,
    })
    .then(({ body }) => body)
